import axios, { AxiosInstance } from 'axios';
import { decode } from 'jsonwebtoken';
import { clearStorage, getStorage, setStorage } from '@/utils/storage';
import { httpError, rtnError } from './error';
import router from '@/plugins/router';

/**
 * 工具类 Axios 实例
 * 支持类型：1. 颜色工具; 2. 定时任务
 * 1. 工具接口前缀不是以 `/api` 开头, 各个工具接口前缀请参考 `根目录/nginx/default.conf` 文件里配置的 `location`
 * 2. 在 `根目录/.env` 配置一个 BaseUrl 环境变量, 方便统一管理（如果各个环境的前缀一致，也可以不配置环境变量）
 * 3. 在 `根目录/vue.config.js` 里配置一个对应接口前缀的代理, 方便本地开发
 */

type ReqType = 'color' | 'cron';
type InsMap = { [K in ReqType]: AxiosInstance };

interface JwtToken {
  sub: string;
  exp: number;
  iat: number;
  data: string;
  client: string;
}

// 最近一次请求的 token
let latestToken = getStorage('token');

// 颜色分析工具
const color = axios.create({
  baseURL: '/colorquantization',
  timeout: 1000 * 10,
});

// 定时任务
const cron = axios.create({
  baseURL: '/cron',
  timeout: 1000 * 10,
});

// 颜色工具请求拦截器
color.interceptors.request.use((config) => (config.headers.Authorization = getStorage('token'), config));

// 颜色工具响应拦截器
color.interceptors.response.use(
  (response) => response.data,
  (error) => (httpError(error), Promise.reject(error)),
);

// 定时任务请求拦截器
cron.interceptors.request.use((config) => (config.headers.Authorization = getStorage('token'), config));

// 定时任务响应拦截器
cron.interceptors.response.use((response) => {
    const res = response.data as { rtnCode: number, data: any, message: string };
    const rtnCode = res.rtnCode;
    const token = response.headers.authorization;
    const ignoreCodes = response.config.ignoreCode || [];

    // 将 token 信息存入 localstorage (仅当 token 发生变更时才存储)
    if (token && token !== latestToken) {
      const tokenStr = token.substring('Bearer'.length);
      const tokenInfo = decode(tokenStr) as JwtToken;
      const userId = JSON.parse(tokenInfo.data || '{}').id;
      latestToken = token;

      if (!!userId) {
        // 正常用户
        setStorage('token', token);
        setStorage('expire', tokenInfo.exp * 1000);
        setStorage('userInfo', tokenInfo.data);
      } else {
        // 用户被锁定后，token 刷新为匿名用户，强制退出登录
        clearStorage();
        router.push({ name: 'login' }).then(() => rtnError(-1000, '当前用户无法正常使用'));
        return;
      }
    }

    // 如果业务错误码是被忽略的，则会正常返回(即使有业务错误，依然会进入 then 分支)
    if (rtnCode && ignoreCodes.indexOf(rtnCode) !== -1) {
      return res;
    }

    // 拦截业务异常(会进入 catch 分支)
    if (rtnCode !== 0) {
      rtnError(rtnCode, res.message);
      return Promise.reject(res);
    }

    return res.data;
  }, (error) => {
    // 如果发送请求时，传入了 ignoreCode 参数，则对应的 http 错误，不会在页面上提示
    const httpCode = error?.response?.status || -1;
    const ignoreCodes = error.config.ignoreCode || [];
    if (ignoreCodes.indexOf(httpCode) === -1) {
      httpError(error);
    }
    return Promise.reject(error);
  },
);

export function tool(type: ReqType) {
  const instanceMap: InsMap = { color, cron };
  return instanceMap[type];
}
